import { GovernmentIssuedWcJurisdictions } from '@evidentid/tprm-portal-lib/models/common';
import { EntityAddress, Producer } from '@evidentid/tprm-portal-lib/models/dashboard';

import { isGovernmentIssuedWcLocation } from '@/modules/entity-details/utils/workersCompLocation';

/** One editable producer in the Producers card.
 *
 * `key` is a client-only identity so Vue can track rows across add/remove without keying on
 * array index (which breaks error placement when a middle row is removed). `isActive` is
 * deliberately absent — it is derived from `coverageTypes` at save time, never edited directly.
 */
export interface ProducerRow {
    key: string;
    producerFirm: string;
    contactName: string;
    email: string;
    phone: string;
    coverageTypes: string[];
    /** Whether the phone input reported the number as parseable. */
    phoneValid: boolean;
    /** Whether this producer is the catch-all recipient. Exactly one row carries it while any
     * row exists; removing that row clears it rather than promoting another, so the replacement
     * is chosen deliberately. */
    isDefault: boolean;
}

export type ProducerRowField = 'producerFirm' | 'contactName' | 'email' | 'phone';

export const PRODUCER_ROW_FIELD_LABELS: Record<ProducerRowField, string> = {
    producerFirm: 'Producer Firm',
    contactName: 'Contact Name',
    email: 'Email',
    phone: 'Phone',
};

export type ProducerRowErrors = Partial<Record<ProducerRowField | 'coverageTypes', string>>;

export const DUPLICATE_EMAIL_MESSAGE = 'This email is already used by another producer.';
export const CONTACT_EMAIL_MESSAGE = 'Producer email cannot be the same as the entity contact email.';
export const NO_COVERAGE_MESSAGE = 'Select at least one coverage type for this producer.';
export const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.';
export const INVALID_PHONE_MESSAGE = 'Enter a valid phone number.';
export const MISSING_DEFAULT_MESSAGE = 'Select a new default producer.';

export const WORKERS_COMPENSATION_TYPE = 'WORKERS_COMPENSATION';

/**
 * The entity's coverage types a producer may be given.
 *
 * Workers compensation is withheld only where the entity's own address makes it government-issued:
 * there the entity obtains the policy itself and the producer's submission screen leaves it out
 * too, so offering it here would demand an assignment the producer could never act on. Anywhere
 * else it is an ordinary private-carrier policy a producer handles like any other coverage.
 */
export function getAssignableCoverageTypes(
    coverageTypes: string[],
    address: Partial<EntityAddress> | null | undefined,
    wcJurisdictions: GovernmentIssuedWcJurisdictions | null | undefined,
): string[] {
    if (!isGovernmentIssuedWcLocation(address, wcJurisdictions)) {
        return coverageTypes;
    }
    return coverageTypes.filter((coverageType) => coverageType !== WORKERS_COMPENSATION_TYPE);
}

export function unassignedCoverageMessage(labels: string[]): string {
    return labels.length === 1
        ? `${labels[0]} is not assigned to any producer.`
        : `These coverage types are not assigned to any producer: ${labels.join(', ')}.`;
}

let rowCounter = 0;

function nextRowKey(): string {
    rowCounter += 1;
    return `producer-row-${rowCounter}`;
}

export function createProducerRow(producer?: Producer): ProducerRow {
    return {
        key: nextRowKey(),
        producerFirm: producer?.producerFirm || '',
        contactName: producer?.contactName || '',
        email: producer?.email || '',
        phone: producer?.phone || '',
        coverageTypes: [ ...(producer?.coverageTypes || []) ],
        // An existing producer's stored number is treated as valid until the user edits it;
        // PhoneInput only reports validity once it handles an input event.
        phoneValid: true,
        isDefault: producer?.isDefault || false,
    };
}

/** Applies the designation a stored list is missing.
 *
 * Picks the first active producer, or the first producer when none is active, matching the backend
 * backfill. Both the editable rows and the comparison in `haveProducersChanged` go through this, so
 * seeding the fallback cannot by itself read as an edit the user did not make. */
export function withDefaultFallback(producers: Producer[]): Producer[] {
    if (producers.length === 0 || producers.some((producer) => producer.isDefault)) {
        return producers;
    }
    const defaultIndex = Math.max(producers.findIndex((producer) => producer.isActive), 0);
    return producers.map((producer, index) => ({ ...producer, isDefault: index === defaultIndex }));
}

export function toProducerRows(producers: Producer[] = []): ProducerRow[] {
    return withDefaultFallback(producers).map((producer) => createProducerRow(producer));
}

/** Moves the default designation to `key`, clearing it everywhere else. */
export function setDefaultProducer(rows: ProducerRow[], key: string): ProducerRow[] {
    return rows.map((row) => ({ ...row, isDefault: row.key === key }));
}

export function hasDefaultProducer(rows: ProducerRow[]): boolean {
    return rows.some((row) => row.isDefault);
}

/** Maps rows back to the API shape. `isActive` mirrors network portal v2: a producer with no
 * coverage types is inactive, and only active producers are notified. */
export function toProducers(rows: ProducerRow[]): Producer[] {
    return rows.map((row) => ({
        producerFirm: row.producerFirm.trim(),
        contactName: row.contactName.trim(),
        email: row.email.trim(),
        phone: row.phone.trim(),
        isActive: row.coverageTypes.length > 0,
        coverageTypes: [ ...row.coverageTypes ],
        isDefault: row.isDefault,
    }));
}

export function normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
}

/**
 * A coverage type belongs to only one producer: assigning it to `key` removes it everywhere else.
 *
 * The edited row keeps exactly what was selected. A type the dropdown no longer offers — workers
 * compensation once the entity's address became a government-issued one, say — therefore falls
 * away on the next edit, which is the intended cleanup: it names a responsibility the producer no
 * longer has. Read mode shows the stored list, so retaining such a value instead would leave it
 * visible but impossible to remove.
 */
export function assignCoverageTypes(rows: ProducerRow[], key: string, selected: string[]): ProducerRow[] {
    const claimed = new Set(selected);
    return rows.map((row) => {
        if (row.key !== key) {
            return { ...row, coverageTypes: row.coverageTypes.filter((type) => !claimed.has(type)) };
        }
        return { ...row, coverageTypes: [ ...selected ] };
    });
}

/** Coverage types from `availableCoverageTypes` that no producer handles. */
export function getUnassignedCoverageTypes(rows: ProducerRow[], availableCoverageTypes: string[]): string[] {
    const assigned = new Set(rows.flatMap((row) => row.coverageTypes));
    return availableCoverageTypes.filter((type) => !assigned.has(type));
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Per-row field errors. Cross-row email rules are resolved here too, so a duplicate flags every
 * row that shares the address rather than only the one being edited.
 *
 * `assignableCoverageTypes` is what the coverage dropdown offers. Assigning one is required while
 * there is something to assign, and optional when there is not — an entity with no risk profile
 * has no coverage types to hand out, and demanding one would leave its producers unsavable. A
 * producer saved without any is inactive, which is a state the API models directly. Omitting the
 * argument keeps the coverage type required, so a caller cannot relax the rule by accident. */
export function validateProducerRows(
    rows: ProducerRow[], entityContactEmail = '', assignableCoverageTypes?: string[],
): Record<string, ProducerRowErrors> {
    const coverageRequired = assignableCoverageTypes === undefined || assignableCoverageTypes.length > 0;
    const normalizedContact = normalizeEmail(entityContactEmail);
    const emailCounts = new Map<string, number>();
    rows.forEach((row) => {
        const normalized = normalizeEmail(row.email);
        if (normalized) {
            emailCounts.set(normalized, (emailCounts.get(normalized) || 0) + 1);
        }
    });

    const result: Record<string, ProducerRowErrors> = {};
    rows.forEach((row) => {
        const errors: ProducerRowErrors = {};
        (Object.keys(PRODUCER_ROW_FIELD_LABELS) as ProducerRowField[]).forEach((field) => {
            if (!row[field].trim()) {
                errors[field] = `${PRODUCER_ROW_FIELD_LABELS[field]} is required.`;
            }
        });

        const normalizedEmail = normalizeEmail(row.email);
        if (!errors.email && !EMAIL_PATTERN.test(normalizedEmail)) {
            errors.email = INVALID_EMAIL_MESSAGE;
        } else if (!errors.email && normalizedContact && normalizedEmail === normalizedContact) {
            errors.email = CONTACT_EMAIL_MESSAGE;
        } else if (!errors.email && (emailCounts.get(normalizedEmail) || 0) > 1) {
            errors.email = DUPLICATE_EMAIL_MESSAGE;
        }

        if (!errors.phone && !row.phoneValid) {
            errors.phone = INVALID_PHONE_MESSAGE;
        }

        if (coverageRequired && row.coverageTypes.length === 0) {
            errors.coverageTypes = NO_COVERAGE_MESSAGE;
        }

        if (Object.keys(errors).length > 0) {
            result[row.key] = errors;
        }
    });
    return result;
}

/** Whether the producer list differs from what the entity currently has, ignoring field
 * whitespace and coverage-type ordering — a reorder is not a change worth confirming. */
export function haveProducersChanged(rows: ProducerRow[], producers: Producer[] = []): boolean {
    // isActive is deliberately excluded: the row model does not own it, it is re-derived from the
    // coverage types on the way out, and the API accepts whatever a caller stored. Comparing a
    // derived value against a stored one would report an untouched entity as edited the moment a
    // stored flag disagreed with its own coverage list.
    const normalize = (list: Producer[]) => JSON.stringify(withDefaultFallback(
        list.map((producer) => ({ ...producer, isDefault: Boolean(producer.isDefault) })),
    ).map((producer) => ({
        producerFirm: (producer.producerFirm || '').trim(),
        contactName: (producer.contactName || '').trim(),
        email: (producer.email || '').trim(),
        phone: (producer.phone || '').trim(),
        coverageTypes: [ ...(producer.coverageTypes || []) ].sort(),
        isDefault: producer.isDefault,
    })));
    return normalize(toProducers(rows)) !== normalize(producers);
}

/** Every producer email on the rows, deduplicated and with blanks dropped. */
export function getProducerEmails(rows: ProducerRow[]): string[] {
    return [ ...new Set(toProducers(rows).map((producer) => producer.email).filter(Boolean)) ];
}

/** Producers present in `rows` that the entity does not already have, matched on email — the
 * people the confirmation window warns will be contacted. */
export function getAddedProducerEmails(rows: ProducerRow[], producers: Producer[] = []): string[] {
    const existing = new Set(producers.map((producer) => normalizeEmail(producer.email || '')));
    return toProducers(rows)
        .filter((producer) => producer.isActive && !existing.has(normalizeEmail(producer.email)))
        .map((producer) => producer.email);
}

/**
 * Whether saving these rows takes the entity from having no active producer to having one.
 *
 * That transition is the one the service answers immediately: it cancels the pending reminder,
 * issues a fresh submission link and can email the producer straight away. Every other producer
 * change waits for the next scheduled send.
 */
export function isFirstActiveProducerTransition(existing: Producer[], rows: ProducerRow[]): boolean {
    return !existing.some((producer) => producer.isActive)
        && toProducers(rows).some((producer) => producer.isActive);
}
